import { Box, Button } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { ErrorMessage, Form, Formik, FormikConfig, FormikProps } from "formik";
import { TFunction } from "i18next";
import moment from "moment/moment";
import React from "react";
import { useTranslation } from "react-i18next";
import SelectSearch, { SelectedOptionValue } from "react-select-search";
import { DatePicker } from "rsuite";
import { isBefore } from "rsuite/esm/internals/utils/date";
import { object, string } from "yup";

import style from "./add-user.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Tooltip from "components/tooltip/Tooltip";
import { EMAIL_MAX_LENGTH } from "domain/globalConstants";
import {
    DEFAULT_LOGIN_METHOD,
    INTEGRATION_USER_TYPE,
    MACHINE_USER_TYPE,
    STANDARD_USER_TYPE,
    UserLoginMethod,
    userLoginMethodList,
    UserType,
    userTypeList,
} from "domain/user";
import { CheckEmailAvailability, Role } from "domain/users";
import { LicensePoolList } from "services/licenses/LicensePoolService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { userService } from "services/user/users/UserService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { createFilterOptions, isStringBlank, isStringNotBlank } from "utils/commonFunctions";
import { formatDate, formatDateWithoutTime, formatTimestamp } from "utils/format";

import alert from "assets/images/icons/alert.svg";

import testIds from "testIds.json";

interface Props {
    fullName?: string;
    email?: string;
    roles?: Role[];
    userRoleUuid?: string;
    userRoleName?: string;
    submitEventHandler?: (values: FormValues) => Promise<void>;
    disableEmail: boolean;
    enabled: boolean;
    disableStatus: boolean;
    expirationDate: string;
    loginMethod?: UserLoginMethod;
    ssoEnabled: boolean;
    licensePools: LicensePoolList[];
    userLicensePoolUuid?: string;
    licensePoolName?: string;
    onView?: boolean;
    userType: UserType;
    lastLogin?: string;
    totalLogins?: number;
}

export interface FormValues {
    fullName: string;
    email: string;
    roleUuid: string;
    enabled: boolean;
    expirationDate: string;
    loginMethod: string;
    licensePool: string;
    userType: string;
}

function deriveDefaultRole(roles: Role[]): Role {
    const defaultRole = roles.find((r) => r.defaultNewUserRole);
    if (defaultRole != null) {
        return defaultRole;
    }
    if (roles.length > 0) {
        return roles[0];
    }
    throw new Error("Unsupported state: there should always be at least three roles");
}

export function generateUserTypesToTranslationMap(t: TFunction) {
    return new Map<UserType, string>([
        [STANDARD_USER_TYPE, t("AddUserForm.userType.standard")],
        [INTEGRATION_USER_TYPE, t("AddUserForm.userType.integration")],
        [MACHINE_USER_TYPE, t("AddUserForm.userType.machine")],
    ]);
}

export default function AddUserForm(props: Props): JSX.Element {
    const { t } = useTranslation();
    const abortController = new AbortController();
    const [loading, setLoading] = React.useState<boolean>(false);
    const oldStatus = props.enabled;
    const [newStatus, setNewStatus] = React.useState<boolean>(oldStatus);
    const [cleaned, setCleaned] = React.useState(false);
    const [existingExpirationDate, setExistingExpirationDate] = React.useState<string>(props.expirationDate);
    const [licensePool, setLicensePool] = React.useState<string>(props.userLicensePoolUuid ?? "");

    const statusChanged = newStatus !== oldStatus;
    const LOGIN_METHODS_TO_TRANSLATION_MAP = new Map<UserLoginMethod, string>([
        [DEFAULT_LOGIN_METHOD, t("Settings.loginSettings.loginMethod.default")],
        ["COMPANY_ID", t("Settings.loginSettings.loginMethod.companyId")],
        ["EMAIL", t("Settings.loginSettings.loginMethod.emailSignIn")],
        ["COMPANY_ID_AND_EMAIL", t("Settings.loginSettings.loginMethod.both")],
    ]);
    const USER_TYPES_TO_TRANSLATION_MAP = generateUserTypesToTranslationMap(t);

    const disableDateBeforePresentDay = () => {
        const disableDate = new Date();
        disableDate.setDate(disableDate.getDate() - 1);
        return disableDate;
    };
    const handleClean = () => {
        setCleaned(true);
        setExistingExpirationDate("");
    };

    function getUserPoolUuid() {
        const defaultPoolUuid = props.licensePools.find((item) => {
            if (item.defaultPool) {
                return item.poolUuid;
            }
        });
        return props.userLicensePoolUuid ? props.userLicensePoolUuid : defaultPoolUuid?.poolUuid;
    }

    const submitHandler: FormikConfig<FormValues>["onSubmit"] = async (values, { setErrors, setSubmitting }) => {
        if (loading || props.submitEventHandler == null) {
            return;
        }
        values.email = values.email.toLowerCase();
        values.expirationDate = existingExpirationDate;
        values.enabled = newStatus;
        values.licensePool = licensePool != undefined ? licensePool : (props.userLicensePoolUuid as string);
        setSubmitting(false);
        try {
            if (!props.disableEmail) {
                setLoading(true);
                const emailAvailability: CheckEmailAvailability = await userService.checkEmailAvailability(
                    values.email,
                    abortController
                );
                if (emailAvailability.emailIsAvailable && !props.disableEmail) {
                    await props.submitEventHandler(values);
                    setLoading(false);
                    setSubmitting(true);
                } else {
                    setLoading(false);
                    if (emailAvailability.emailIsUndeliverable) {
                        if (emailAvailability.errorMessage === "domainNameCheckingError") {
                            setErrors({ email: t("Common.domainNameCheckingError") });
                        } else {
                            setErrors({
                                email: t("Common." + emailAvailability.errorMessage, {
                                    domain_name: emailAvailability.domainName,
                                }),
                            });
                        }
                    } else {
                        setErrors({ email: t("Common.emailNotAvailable") });
                    }
                }
            } else {
                await props.submitEventHandler(values);
                setSubmitting(true);
            }
        } catch {
            setLoading(false);
            setErrors({ email: t("Common.emailNotAvailable") });
        }
    };
    const handleOkClick = (date: Date) => {
        setCleaned(false);
        setExistingExpirationDate(formatDate(moment(date)));
    };

    return (
        <Formik
            initialValues={{
                email: props.email ?? "",
                fullName: props.fullName ?? "",
                roleUuid: props.disableEmail || !props.roles ? props.userRoleUuid : deriveDefaultRole(props.roles).uuid,
                expirationDate: existingExpirationDate,
                enabled: props.enabled,
                loginMethod: props.loginMethod,
                licensePool: getUserPoolUuid(),
                userType: props.userType,
            }}
            onSubmit={submitHandler}
            validationSchema={object().shape({
                fullName: string().max(255),
                email: string()
                    .required(t("AddUserForm.emailRequired"))
                    .email(t("Common.invalidEmail"))
                    .max(EMAIL_MAX_LENGTH)
                    .test("email-tagging", t("Common.invalidEmailTagging"), (value) => {
                        if (!value) {
                            return true;
                        }
                        const localPart = value.split("@")[0];
                        const plusCount = (localPart.match(/\+/g) || []).length;

                        // Allow maximum one + for tagging
                        if (plusCount > 1) {
                            return false;
                        }

                        return true;
                    })
                    .test("email-plus-ending", t("Common.emailCannotEndWithPlus"), (value) => {
                        if (!value) {
                            return true;
                        }
                        const localPart = value.split("@")[0];
                        return !localPart.endsWith("+");
                    })
                    .test("email-plus-starting", t("Common.emailCannotStartWithPlus"), (value) => {
                        if (!value) {
                            return true;
                        }
                        const localPart = value.split("@")[0];
                        return !localPart.startsWith("+");
                    }),
            })}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {({ values, errors, isSubmitting, handleChange, handleBlur }: FormikProps<FormValues>) => {
                if (isSubmitting) {
                    return <LoadingIndicator />;
                }

                const calendar = document.getElementsByClassName("rs-picker-toggle-caret rs-icon")[0] as HTMLElement;
                if (calendar) {
                    calendar.setAttribute("aria-label", t("AltText.calendar"));
                }

                return (
                    <Form className={classNames(props.onView && form.readOnlyForm)}>
                        <div className={form.formFields}>
                            <div className={form.formFieldsColumnDirection}>
                                <span className={form.optional}>{t("Common.optional")}</span>
                                <label htmlFor="fullName" className={form.label}>
                                    {t("AddUserForm.fullName")}
                                </label>
                            </div>
                            {props.onView ? (
                                <span data-testid={testIds.workArea.user.viewUserDialog.general.nameLabel}>
                                    {values.fullName}
                                </span>
                            ) : (
                                <div className={form.formFieldsColumnDirection}>
                                    <input
                                        id="fullName"
                                        className={classNames(form.input, form.fixedWidthInput)}
                                        maxLength={255}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.fullName}
                                        autoFocus
                                        data-testid={testIds.workArea.user.addUserDialog.nameInput.itself}
                                    />
                                    <div className={form.error}>
                                        <ErrorMessage name="fullName" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={form.formFields}>
                            {!props.disableEmail ? (
                                <>
                                    <label
                                        htmlFor="email"
                                        className={form.label + (errors.email ? " " + form.inputError : "")}
                                    >
                                        {t("Common.emailWithColon")}
                                    </label>
                                    <div className={form.formFieldsColumnDirection}>
                                        <input
                                            id="email"
                                            className={classNames(form.input, form.fixedWidthInput, {
                                                [form.inputError]: errors.email,
                                            })}
                                            maxLength={EMAIL_MAX_LENGTH}
                                            onChange={handleChange}
                                            value={values.email}
                                            data-testid={testIds.workArea.user.addUserDialog.emailInput.itself}
                                            onBlur={() => submitHandler}
                                        />

                                        <div
                                            className={form.error}
                                            data-testid={testIds.workArea.user.addUserDialog.emailInput.errorLabel}
                                        >
                                            <ErrorMessage name="email" />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <label htmlFor="email" className={form.label}>
                                        {t("Common.emailWithColon")}
                                    </label>
                                    {props.onView ? (
                                        <span data-testid={testIds.workArea.user.viewUserDialog.general.emailLabel}>
                                            {values.email}
                                        </span>
                                    ) : (
                                        <input
                                            id="email"
                                            className={classNames(
                                                style.emailInputBox,
                                                form.input,
                                                form.fixedWidthInput
                                            )}
                                            value={values.email}
                                            data-testid={testIds.workArea.user.addUserDialog.emailInput.itself}
                                            onBlur={() => submitHandler}
                                            disabled
                                        />
                                    )}
                                </>
                            )}
                        </div>
                        <div className={form.formFields}>
                            <label htmlFor="role" className={form.label}>
                                {t("AddUserForm.role")}
                            </label>
                            {props.onView ? (
                                <span data-testid={testIds.workArea.user.viewUserDialog.general.roleLabel}>
                                    {props.userRoleName}
                                </span>
                            ) : (
                                <div className={form.formFieldsColumnDirection}>
                                    <select
                                        id={"roleUuid"}
                                        value={values.roleUuid}
                                        data-testid={testIds.workArea.user.addUserDialog.roleSelect.itself}
                                        onChange={handleChange}
                                        className={classNames(form.select, form.fixedWidthInput)}
                                    >
                                        {props.roles != null &&
                                            props.roles
                                                .sort((first, second) => first.name.localeCompare(second.name))
                                                .map((role) => (
                                                    <option key={role.uuid} value={role.uuid}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                    </select>
                                    {/*
                                    It's impossible to get an error but the
                                    element itself takes space in the UI. When
                                    all form fields have it, they have
                                    equivalent amount of space below them. Just
                                    an easy and maintainable way to keep the
                                    form field spacing consistent.
                                */}
                                    <div className={form.error}>
                                        <ErrorMessage name="role" />
                                    </div>
                                </div>
                            )}
                        </div>
                        {tenantHasFeatureLicense("FEATURE_LICENSE_POOLS") &&
                            userSessionService.userHasAnyLicensePoolAuthority() &&
                            (props.onView ? null : (
                                <>
                                    <div className={form.formFields}>
                                        <label htmlFor="licensePool" className={form.label}>
                                            {t("AddUserForm.assignLicensePool")}
                                        </label>
                                        <div className={form.formFieldsColumnDirection}>
                                            <SelectSearch
                                                id={"poolName"}
                                                options={props.licensePools
                                                    .sort((a, b) => a.poolName.localeCompare(b.poolName))
                                                    .map((name) => ({
                                                        value: name.poolName,
                                                        name: name.poolName,
                                                    }))}
                                                filterOptions={createFilterOptions}
                                                search={true}
                                                placeholder={props.licensePoolName ?? t("LicenseAlerts.searchPool")}
                                                onChange={(event: SelectedOptionValue) => {
                                                    setLicensePool(event as unknown as string);
                                                    handleChange(event as unknown as string);
                                                }}
                                                className={classNames(
                                                    form.input,
                                                    form.selectSearch,
                                                    form.fixedWidthInput
                                                )}
                                                value={licensePool ?? props.userLicensePoolUuid}
                                                closeOnSelect={true}
                                                data-testid={
                                                    testIds.workArea.user.addUserDialog.licensePoolSelect.itself
                                                }
                                            />
                                            <div className={form.error}>
                                                <ErrorMessage name="poolName" />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ))}
                        {props.ssoEnabled && (
                            <div className={form.formFields}>
                                <label htmlFor="loginMethod" className={form.label}>
                                    {t("Settings.loginSettings.loginMethod.label")}
                                </label>
                                {props.onView ? (
                                    <span data-testid={testIds.workArea.user.viewUserDialog.general.loginMethodLabel}>
                                        {LOGIN_METHODS_TO_TRANSLATION_MAP.get(values.loginMethod)}
                                    </span>
                                ) : (
                                    <select
                                        id={"loginMethod"}
                                        value={values.loginMethod}
                                        data-testid={testIds.workArea.user.addUserDialog.loginMethodSelect}
                                        onChange={handleChange}
                                        className={classNames(form.select, form.fixedWidthInput)}
                                    >
                                        {userLoginMethodList.map((each) => (
                                            <option key={each} value={each}>
                                                {LOGIN_METHODS_TO_TRANSLATION_MAP.get(each)}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}
                        {
                            <div className={form.formFields}>
                                <label htmlFor="userType" className={form.label}>
                                    {t("AddUserForm.userType.label")}
                                </label>
                                {props.onView ? (
                                    <span data-testid={testIds.workArea.user.viewUserDialog.general.userTypeLabel}>
                                        {USER_TYPES_TO_TRANSLATION_MAP.get(values.userType)}
                                    </span>
                                ) : (
                                    <select
                                        id={"userType"}
                                        value={values.userType}
                                        data-testid={testIds.workArea.user.addUserDialog.userTypeSelect}
                                        onChange={handleChange}
                                        className={classNames(form.select, form.fixedWidthInput)}
                                    >
                                        {userTypeList.map((each) => (
                                            <option key={each} value={each}>
                                                {USER_TYPES_TO_TRANSLATION_MAP.get(each)}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        }

                        {props.disableStatus && (
                            <div className={form.formFields}>
                                <div className={form.formFieldsColumnDirection}>
                                    <span className={form.optional}>{t("Common.optional")}</span>
                                    <label htmlFor="enabled" className={form.label}>
                                        {t("Common.active")}:
                                    </label>
                                </div>
                                <Box verticalAlignment="Center">
                                    <label className={form.container}>
                                        <input
                                            type="checkbox"
                                            name="enabled"
                                            defaultChecked={oldStatus}
                                            className={form.input}
                                            onChange={(event) => {
                                                setNewStatus(event.target.checked);
                                                if (event.target.checked) {
                                                    setExistingExpirationDate("");
                                                }
                                                usageStatisticsService.sendEvent({
                                                    category: Category.USER,
                                                    action: Action.ENABLE_USER,
                                                    label: newStatus.toString(),
                                                });
                                            }}
                                            data-testid={
                                                props.onView
                                                    ? testIds.workArea.user.viewUserDialog.general.statusCheckbox
                                                    : testIds.workArea.user.addUserDialog.statusCheckbox.itself
                                            }
                                            disabled={props.onView}
                                        />
                                        <span className={form.checkmark} />
                                    </label>
                                    {props.onView ?? (
                                        <Tooltip
                                            content={
                                                <div className={style.warningMessage}>
                                                    {t("EditUserForm.warningTooltip")}
                                                </div>
                                            }
                                            placement={"right"}
                                        >
                                            <div className={style.warningIcon} tabIndex={0}>
                                                <img src={alert} alt={t("AltText.alertIcon")} />
                                            </div>
                                        </Tooltip>
                                    )}
                                </Box>
                            </div>
                        )}
                        {
                            <div className={form.formFields}>
                                <div className={form.formFieldsColumnDirection}>
                                    <span className={form.optional}>{t("Common.optional")}</span>
                                    <label htmlFor="expirationDate" className={form.label}>
                                        {t("Common.accessExpiry")}:
                                    </label>
                                </div>
                                {props.onView ? (
                                    <span
                                        data-testid={testIds.workArea.user.viewUserDialog.general.accessExpirationLabel}
                                    >
                                        {isStringBlank(existingExpirationDate)
                                            ? t("Common.never")
                                            : `${formatDateWithoutTime(props.expirationDate)}`}
                                    </span>
                                ) : (
                                    <div className={form.formFieldsColumnDirection}>
                                        <DatePicker
                                            id="expirationDate"
                                            className={form.dateRange}
                                            placement={"topStart"}
                                            title={t("AltText.date")}
                                            onSelect={handleOkClick}
                                            placeholder={t("Common.never")}
                                            disabledDate={(date: Date) => isBefore(date, disableDateBeforePresentDay())}
                                            ranges={[
                                                {
                                                    label: "Today",
                                                    value: new Date(),
                                                    closeOverlay: true,
                                                },
                                            ]}
                                            renderValue={(value: Date) => {
                                                return isStringBlank(existingExpirationDate)
                                                    ? t("Common.never")
                                                    : `${formatDateWithoutTime(value.toString())}`;
                                            }}
                                            value={
                                                isStringNotBlank(existingExpirationDate) && !cleaned
                                                    ? new Date(existingExpirationDate.split("T")[0])
                                                    : null
                                            }
                                            onClean={handleClean}
                                            disabled={statusChanged === oldStatus}
                                        />
                                        <div className={form.error}>
                                            <ErrorMessage name="expirationDate" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        }
                        {props.onView ? (
                            <div className={form.formFields}>
                                <label htmlFor="lastLogin" className={form.label}>
                                    {t("AddUserForm.lastLogin")}
                                </label>
                                <span data-testid={testIds.workArea.user.viewUserDialog.general.lastLoginLabel}>
                                    {props.lastLogin ? formatTimestamp(props.lastLogin) : t("Common.na")}
                                </span>
                            </div>
                        ) : null}
                        {props.onView ? (
                            <div className={form.formFields}>
                                <label htmlFor="totalLogins" className={form.label}>
                                    {t("AddUserForm.loginCount")}
                                </label>
                                <span data-testid={testIds.workArea.user.viewUserDialog.general.loginCountLabel}>
                                    {props.totalLogins ? props.totalLogins.toString() : t("Common.na")}
                                </span>
                            </div>
                        ) : null}
                        {props.onView ?? (
                            <div className={form.buttonContainer}>
                                {!props.disableEmail ? (
                                    <Button
                                        className={buttons.primaryButtonWithoutIcon}
                                        isLoading={loading || isSubmitting}
                                        type="submit"
                                        variant={"Primary"}
                                        disabled={loading || isSubmitting}
                                        testId={testIds.workArea.user.addUserDialog.submitButton}
                                    >
                                        {t("AddUserView.userButton")}
                                    </Button>
                                ) : (
                                    <Button
                                        className={buttons.primaryButtonWithoutIcon}
                                        isLoading={loading || isSubmitting}
                                        type="submit"
                                        variant={"Primary"}
                                        disabled={loading || isSubmitting}
                                        testId={testIds.workArea.user.editUserDialog.saveButton}
                                    >
                                        {t("Common.save")}
                                    </Button>
                                )}
                            </div>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
}
